import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import toast from 'react-hot-toast';


// Async thunks for analytics
export const fetchDealInsights = createAsyncThunk(
    'analytics/fetchDealInsights',
    async (filters = {}, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams(filters).toString();
            const response = await axios.get(`crm/analytics/deal-insights?${params}`);
            return response.data;
        } catch (error) {
            toast.error('Failed to fetch deal insights');
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchDemographics = createAsyncThunk(
    'analytics/fetchDemographics',
    async ({ type = 'all', filters = {} }, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams({ type, ...filters }).toString();
            const response = await axios.get(`crm/analytics/demographics?${params}`);
            return response.data;
        } catch (error) {
            toast.error('Failed to fetch demographics');
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchPerformance = createAsyncThunk(
    'analytics/fetchPerformance',
    async ({ type = 'all', filters = {} }, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams({ type, ...filters }).toString();
            const response = await axios.get(`crm/analytics/performance?${params}`);
            return response.data;
        } catch (error) {
            toast.error('Failed to fetch performance analytics');
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchCampaignAnalytics = createAsyncThunk(
    'analytics/fetchCampaignAnalytics',
    async (filters = {}, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams(filters).toString();
            const response = await axios.get(`crm/analytics/campaign?${params}`);
            return response.data;
        } catch (error) {
            toast.error('Failed to fetch campaign analytics');
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const executeCustomAnalytics = createAsyncThunk(
    'analytics/executeCustomAnalytics',
    async (config, { rejectWithValue }) => {
        try {
            const response = await axios.post(`crm/analytics/custom`, config);
            return response.data;
        } catch (error) {
            toast.error('Failed to execute custom analytics');
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchSavedAnalytics = createAsyncThunk(
    'analytics/fetchSavedAnalytics',
    async ({ includePublic = false, templatesOnly = false } = {}, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams({ includePublic, templatesOnly }).toString();
            const response = await axios.get(`crm/analytics/saved?${params}`);
            return response.data;
        } catch (error) {
            toast.error('Failed to fetch saved analytics');
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const saveAnalytics = createAsyncThunk(
    'analytics/saveAnalytics',
    async (analyticData, { rejectWithValue }) => {
        try {
            const response = await axios.post(`crm/analytics/saved`, analyticData);
            return response.data;
        } catch (error) {
            toast.error('Failed to save analytics');
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateAnalytics = createAsyncThunk(
    'analytics/updateAnalytics',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await axios.put(`crm/analytics/saved/${id}`, data);
            return response.data;
        } catch (error) {
            toast.error('Failed to update analytics');
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const deleteAnalytics = createAsyncThunk(
    'analytics/deleteAnalytics',
    async (id, { rejectWithValue }) => {
        try {
            await axios.delete(`crm/analytics/saved/${id}`);
            return id;
        } catch (error) {
            toast.error('Failed to delete analytics');
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchTemplates = createAsyncThunk(
    'analytics/fetchTemplates',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`crm/analytics/templates`);
            return response.data;
        } catch (error) {
            toast.error('Failed to fetch templates');
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const initialState = {
    dealInsights: null,
    demographics: null,
    performance: null,
    campaignAnalytics: null,
    customAnalytics: null,
    savedAnalytics: [],
    templates: [],
    loading: false,
    error: null,
    // Loading states for different sections
    loadingDealInsights: false,
    loadingDemographics: false,
    loadingPerformance: false,
    loadingCampaign: false,
    loadingCustom: false,
    loadingSaved: false,
};

const analyticsSlice = createSlice({
    name: 'analytics',
    initialState,
    reducers: {
        clearAnalyticsData: (state) => {
            state.dealInsights = null;
            state.demographics = null;
            state.performance = null;
            state.campaignAnalytics = null;
            state.customAnalytics = null;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Deal Insights
        builder
            .addCase(fetchDealInsights.pending, (state) => {
                state.loadingDealInsights = true;
                state.error = null;
            })
            .addCase(fetchDealInsights.fulfilled, (state, action) => {
                state.loadingDealInsights = false;
                state.dealInsights = action.payload;
            })
            .addCase(fetchDealInsights.rejected, (state, action) => {
                state.loadingDealInsights = false;
                state.error = action.payload;
            });

        // Demographics
        builder
            .addCase(fetchDemographics.pending, (state) => {
                state.loadingDemographics = true;
                state.error = null;
            })
            .addCase(fetchDemographics.fulfilled, (state, action) => {
                state.loadingDemographics = false;
                state.demographics = action.payload;
            })
            .addCase(fetchDemographics.rejected, (state, action) => {
                state.loadingDemographics = false;
                state.error = action.payload;
            });

        // Performance
        builder
            .addCase(fetchPerformance.pending, (state) => {
                state.loadingPerformance = true;
                state.error = null;
            })
            .addCase(fetchPerformance.fulfilled, (state, action) => {
                state.loadingPerformance = false;
                state.performance = action.payload;
            })
            .addCase(fetchPerformance.rejected, (state, action) => {
                state.loadingPerformance = false;
                state.error = action.payload;
            });

        // Campaign Analytics
        builder
            .addCase(fetchCampaignAnalytics.pending, (state) => {
                state.loadingCampaign = true;
                state.error = null;
            })
            .addCase(fetchCampaignAnalytics.fulfilled, (state, action) => {
                state.loadingCampaign = false;
                state.campaignAnalytics = action.payload;
            })
            .addCase(fetchCampaignAnalytics.rejected, (state, action) => {
                state.loadingCampaign = false;
                state.error = action.payload;
            });

        // Custom Analytics
        builder
            .addCase(executeCustomAnalytics.pending, (state) => {
                state.loadingCustom = true;
                state.customAnalytics = null; // Clear previous data
                state.error = null;
            })
            .addCase(executeCustomAnalytics.fulfilled, (state, action) => {
                state.loadingCustom = false;
                state.customAnalytics = action.payload;
            })
            .addCase(executeCustomAnalytics.rejected, (state, action) => {
                state.loadingCustom = false;
                state.error = action.payload;
            });

        // Saved Analytics
        builder
            .addCase(fetchSavedAnalytics.pending, (state) => {
                state.loadingSaved = true;
                state.error = null;
            })
            .addCase(fetchSavedAnalytics.fulfilled, (state, action) => {
                state.loadingSaved = false;
                state.savedAnalytics = action.payload;
            })
            .addCase(fetchSavedAnalytics.rejected, (state, action) => {
                state.loadingSaved = false;
                state.error = action.payload;
            });

        // Save Analytics
        builder
            .addCase(saveAnalytics.pending, (state) => {
                state.loading = true;
            })
            .addCase(saveAnalytics.fulfilled, (state, action) => {
                state.loading = false;
                state.savedAnalytics.push(action.payload);
            })
            .addCase(saveAnalytics.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Update Analytics
        builder
            .addCase(updateAnalytics.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateAnalytics.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.savedAnalytics.findIndex(a => a.id === action.payload.id);
                if (index !== -1) {
                    state.savedAnalytics[index] = action.payload;
                }
            })
            .addCase(updateAnalytics.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Delete Analytics
        builder
            .addCase(deleteAnalytics.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteAnalytics.fulfilled, (state, action) => {
                state.loading = false;
                state.savedAnalytics = state.savedAnalytics.filter(a => a.id !== action.payload);
            })
            .addCase(deleteAnalytics.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Templates
        builder
            .addCase(fetchTemplates.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchTemplates.fulfilled, (state, action) => {
                state.loading = false;
                state.templates = action.payload;
            })
            .addCase(fetchTemplates.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { clearAnalyticsData, clearError } = analyticsSlice.actions;
export default analyticsSlice.reducer;
